import React, { useState } from 'react';
import { Text, Stack, Button } from 'basis';
import { LayoutPage, LayoutSection, LayoutOutcomePage, Error as ErrorComp } from 'components';
import { ApiError } from 'types';
import { useTheme } from 'theme';
import { useAppContext, useAssistedApplyAcceptOffer, useTrackingError, useTrackingEvent } from 'hooks';
import { setError } from 'context';

export const AssistedApplyPendingAcceptance: React.FC = () => {
  const theme = useTheme();
  const { state, dispatch } = useAppContext();
  const { trackEvent } = useTrackingEvent();
  const { trackError } = useTrackingError();
  const [accepted, setAccepted] = useState(false);

  const handleSubmitSuccess = () => {
    trackEvent({
      category: 'application',
      action: 'application-navigation',
      location: 'assisted-apply-pca',
      label: 'acceptance-confirmed',
    });
    setAccepted(true);
  };

  const handleSubmitError = (error: ApiError) => {
    dispatch(setError(error));
    trackError({ error });
  };

  const { loading, acceptOffer } = useAssistedApplyAcceptOffer({
    onSuccess: handleSubmitSuccess,
    onError: handleSubmitError,
  });

  const onAcceptClick = () => {
    trackEvent({
      category: 'application',
      action: 'application-navigation',
      location: 'assisted-apply-pca',
      label: 'accept-offer',
    });
    acceptOffer();
  };

  if (state.error) {
    return (
      <LayoutPage step="login">
        <ErrorComp retrying={loading} />
      </LayoutPage>
    );
  }

  if (accepted) {
    return (
      <LayoutPage step="outcome" paddingBottom={0}>
        <LayoutOutcomePage useConfetti>
          <Stack gap="8">
            <Text as="h1" textStyle="heading5" textStyle-md="heading4" color={theme.headingColor}>
              Thank you for accepting the offer
            </Text>
            <Text>
              We've sent you an email to {state.contactDetails.emailAddress} with your Initial Disclosure
              Statement, Conditions of Use and account details. You'll need to keep these documents for
              future reference.
            </Text>
          </Stack>
        </LayoutOutcomePage>
      </LayoutPage>
    );
  }

  return (
    <LayoutPage step="update">
      <LayoutSection>
        <Stack gap="8">
          <Text as="h1" textStyle="heading5" textStyle-md="heading4" color={theme.headingColor}>
            Accept your offer
          </Text>
          <Text>
            I acknowledge receipt of the Initial Disclosure Statement and Conditions of Use, and confirm
            my acceptance of the offer.
          </Text>
          <Button type="button" onClick={onAcceptClick} loading={loading}>
            Accept Offer
          </Button>
        </Stack>
      </LayoutSection>
    </LayoutPage>
  );
};
